import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Tv, 
  Globe, 
  Trophy, 
  Film, 
  Smartphone, 
  FileCheck,
  ChevronRight,
  Check,
  Play,
  Zap,
  Users,
  Radio,
  Baby,
  Newspaper,
  Star,
  Quote,
  Monitor
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NAV Component
const Navbar = ({ onScrollTo }) => {
  return (
    <nav className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-['Playfair_Display'] text-2xl font-bold text-white">
            Prestigious<span className="text-[#D4AF37]">TV</span>
          </span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => onScrollTo('hero')} className="nav-link" data-testid="nav-home">Home</button>
          <button onClick={() => onScrollTo('pricing')} className="nav-link" data-testid="nav-pricing">Pricing</button>
          <button onClick={() => onScrollTo('faq')} className="nav-link" data-testid="nav-faq">FAQ</button>
          <Link to="/admin" className="nav-link" data-testid="nav-login">Login</Link>
        </div>
      </div>
    </nav>
  );
};

// HERO Section - Entertainment Focused
const HeroSection = ({ heroRef, onScrollTo }) => {
  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Entertainment Collage Background */}
      <div className="absolute inset-0 grid grid-cols-4 gap-1 opacity-30">
        <div className="bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=300&fit=crop')` }} />
        <div className="bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&h=300&fit=crop')` }} />
        <div className="bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=400&h=300&fit=crop')` }} />
        <div className="bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1461151304267-38535e780c79?w=400&h=300&fit=crop')` }} />
      </div>
      
      {/* Gradient Overlay */}
      <div className="hero-overlay absolute inset-0" />
      
      {/* Animated Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#8B5CF6]/20 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00D4FF]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-8 animate-fade-in">
          <Zap className="w-4 h-4 text-[#00D4FF]" />
          <span className="text-sm text-white/70">50,000+ Live Channels</span>
        </div>
        
        <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-black tracking-tight leading-none mb-6 animate-fade-in">
          Everything You Love.<br />
          <span className="text-gold-gradient">All in One Place.</span>
        </h1>
        
        <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-10 animate-fade-in stagger-1">
          Live TV, sports, movies, and global channels — stream it all anytime, anywhere.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-2">
          <button 
            onClick={() => onScrollTo('signup')}
            className="btn-primary text-lg animate-pulse-gold inline-flex items-center gap-2"
            data-testid="hero-cta"
          >
            <Play className="w-5 h-5" /> Start Streaming <ChevronRight className="w-5 h-5" />
          </button>
          <button 
            onClick={() => onScrollTo('pricing')}
            className="btn-secondary text-lg inline-flex items-center gap-2"
          >
            View Plans
          </button>
        </div>
        
        <p className="mt-6 text-white/50 text-sm animate-fade-in stagger-3">
          No contracts. No hidden fees. Cancel anytime.
        </p>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
          <div className="w-1.5 h-3 bg-[#00D4FF] rounded-full" />
        </div>
      </div>
    </section>
  );
};

// POPULAR CATEGORIES Section
const CategoriesSection = () => {
  const categories = [
    { name: "Live Sports", icon: Trophy, image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=300&h=400&fit=crop", color: "#D4AF37" },
    { name: "Movies", icon: Film, image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=300&h=400&fit=crop", color: "#8B5CF6" },
    { name: "Kids & Family", icon: Baby, image: "https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=300&h=400&fit=crop", color: "#00D4FF" },
    { name: "News", icon: Newspaper, image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=300&h=400&fit=crop", color: "#10B981" },
    { name: "International", icon: Globe, image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=300&h=400&fit=crop", color: "#F59E0B" },
    { name: "Premium", icon: Star, image: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=300&h=400&fit=crop", color: "#D4AF37" },
  ];
  
  return (
    <section className="py-16 px-6 bg-section-gradient" data-testid="categories-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[#00D4FF] uppercase tracking-widest text-sm mb-2">Explore</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold">
              Popular Categories
            </h2>
          </div>
        </div>
        
        <div className="scroll-container">
          {categories.map((cat, idx) => (
            <div 
              key={idx}
              className="category-card w-[200px] h-[280px] relative group"
              data-testid={`category-${idx}`}
            >
              <img 
                src={cat.image} 
                alt={cat.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 z-10 flex flex-col justify-end p-4">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${cat.color}20`, border: `1px solid ${cat.color}50` }}
                >
                  <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                </div>
                <h3 className="font-semibold text-white text-lg">{cat.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FEATURES Section
const FeaturesSection = () => {
  const features = [
    { icon: Globe, title: "50,000+ Channels", desc: "Worldwide content at your fingertips", color: "#D4AF37" },
    { icon: Trophy, title: "Live Sports & Events", desc: "Premium sports and live events", color: "#00D4FF" },
    { icon: Film, title: "Movies & TV On Demand", desc: "Thousands of titles available", color: "#8B5CF6" },
    { icon: Smartphone, title: "Multi-Device Support", desc: "Firestick, Smart TVs, Phones, Tablets", color: "#10B981" },
    { icon: FileCheck, title: "No Contracts", desc: "Cancel anytime, no obligations", color: "#F59E0B" },
  ];
  
  return (
    <section className="py-24 px-6 bg-section-alt" data-testid="features-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Why Choose Us</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            Premium Entertainment
          </h2>
          <div className="section-divider" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="feature-card card p-8 group border border-white/10"
              data-testid={`feature-card-${idx}`}
            >
              <div 
                className="w-14 h-14 flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                style={{ 
                  backgroundColor: `${feature.color}15`,
                  border: `1px solid ${feature.color}30`,
                  boxShadow: `0 0 20px ${feature.color}10`
                }}
              >
                <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-white/60">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// VALUE Section
const ValueSection = () => {
  const values = [
    "Replace expensive cable bills",
    "No equipment rentals",
    "Instant access",
    "Stream anywhere"
  ];
  
  return (
    <section className="py-24 px-6 bg-section-gradient" data-testid="value-section">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-[#00D4FF] uppercase tracking-widest text-sm mb-4">The Smart Choice</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6">
            Save Money,<br />Get More
          </h2>
          <ul className="space-y-4">
            {values.map((value, idx) => (
              <li key={idx} className="flex items-center gap-4 group">
                <div className="w-8 h-8 bg-gradient-to-br from-[#D4AF37]/20 to-[#00D4FF]/20 border border-[#D4AF37]/30 flex items-center justify-center transition-transform group-hover:scale-110">
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <span className="text-white/80 text-lg">{value}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <img 
            src="https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=600&h=400&fit=crop"
            alt="Streaming experience"
            className="w-full h-auto border border-white/10 rounded-lg"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent rounded-lg" />
          
          {/* Floating badge */}
          <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-[#1E1B4B] to-[#121212] border border-[#D4AF37]/30 p-4 animate-float">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white font-bold">1M+</p>
                <p className="text-white/50 text-sm">Happy Viewers</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// HOW IT WORKS Section
const HowItWorksSection = () => {
  const steps = [
    { num: "01", title: "Choose Your Plan", desc: "Select our annual access plan", icon: FileCheck },
    { num: "02", title: "Receive Login", desc: "Get your credentials instantly", icon: Radio },
    { num: "03", title: "Start Streaming", desc: "Enjoy 50,000+ channels", icon: Play },
  ];
  
  return (
    <section className="py-24 px-6 bg-section-alt" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Simple Process</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            How It Works
          </h2>
          <div className="section-divider" />
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="text-center group" data-testid={`step-${idx}`}>
              <div className="relative inline-block mb-6">
                <div className="text-[#8B5CF6]/20 font-['Playfair_Display'] text-8xl font-bold absolute -top-4 -left-4">
                  {step.num}
                </div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-[#1E1B4B] to-[#121212] border border-[#D4AF37]/30 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:border-[#00D4FF]/50">
                  <step.icon className="w-8 h-8 text-[#D4AF37] group-hover:text-[#00D4FF] transition-colors" />
                </div>
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-2">{step.title}</h3>
              <p className="text-white/60">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// PRICING Section
const PricingSection = ({ pricingRef, onScrollTo, annualPrice }) => {
  return (
    <section ref={pricingRef} className="py-24 px-6 bg-section-gradient" data-testid="pricing-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Pricing</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            One Plan. Full Access.
          </h2>
          <div className="section-divider" />
        </div>
        
        <div className="pricing-glow card p-10 text-center max-w-lg mx-auto border border-[#D4AF37]/20 relative overflow-hidden">
          {/* Glow effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-[#D4AF37]/20 rounded-full blur-[80px]" />
          
          <div className="relative">
            <span className="badge-included inline-block mb-4">1 Device Included</span>
            
            <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-2">PTV Annual Access</h3>
            <div className="flex items-end justify-center gap-1 my-6">
              <span className="text-5xl md:text-6xl font-bold text-gold-gradient">${annualPrice}</span>
              <span className="text-white/60 mb-2">/year</span>
            </div>
            
            <ul className="space-y-4 text-left mb-8">
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#D4AF37]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-white/80">Full access to all channels</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#D4AF37]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-white/80">All core channels included</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#D4AF37]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-white/80">Multi-device support</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#D4AF37]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                </div>
                <span className="text-white/80">24/7 streaming availability</span>
              </li>
            </ul>
            
            <button 
              onClick={() => onScrollTo('signup')}
              className="btn-primary w-full"
              data-testid="pricing-cta"
            >
              Get Started
            </button>
            
            <p className="mt-4 text-[#00D4FF]/70 text-sm">
              Add more devices anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ADD-ONS Section
const AddonsSection = ({ selectedAddons, setSelectedAddons }) => {
  const addons = [
    { id: "additional-device", name: "Additional Device Access", desc: "Add more devices to your plan", icon: Smartphone },
    { id: "premium-sports", name: "Premium Sports Package", desc: "Exclusive sports channels & events", icon: Trophy },
    { id: "international", name: "International Channel Boost", desc: "Extra international content", icon: Globe },
    { id: "vip-support", name: "VIP Priority Support", desc: "24/7 dedicated support team", icon: Star },
  ];
  
  const toggleAddon = (id) => {
    setSelectedAddons(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };
  
  return (
    <section className="py-24 px-6 bg-section-alt" data-testid="addons-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-[#00D4FF] uppercase tracking-widest text-sm mb-4">Enhance Your Plan</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            Upgrade Your Experience
          </h2>
          <div className="section-divider" />
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {addons.map((addon) => (
            <div 
              key={addon.id}
              className={`addon-checkbox group ${selectedAddons.includes(addon.id) ? 'selected' : ''}`}
              onClick={() => toggleAddon(addon.id)}
              data-testid={`addon-${addon.id}`}
            >
              <div className={`w-6 h-6 border mr-4 flex items-center justify-center transition-all ${
                selectedAddons.includes(addon.id) 
                  ? 'bg-gradient-to-br from-[#D4AF37] to-[#00D4FF] border-transparent' 
                  : 'border-white/30 group-hover:border-[#D4AF37]/50'
              }`}>
                {selectedAddons.includes(addon.id) && <Check className="w-4 h-4 text-black" />}
              </div>
              <addon.icon className={`w-5 h-5 mr-3 transition-colors ${
                selectedAddons.includes(addon.id) ? 'text-[#D4AF37]' : 'text-white/40 group-hover:text-[#00D4FF]'
              }`} />
              <div>
                <h4 className="font-semibold text-white">{addon.name}</h4>
                <p className="text-sm text-white/50">{addon.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SIGNUP Section
const SignupSection = ({ signupRef, selectedAddons, annualPrice, promoCodesEnabled }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  
  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/validate-promo`, {
        code: promoCode,
        original_price: annualPrice
      });
      setPromoApplied(response.data);
      toast.success(`Promo code applied! You save $${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/ptv-signup`, {
        ...formData,
        addons: selectedAddons
      });
      
      toast.success("Welcome to Prestigious TV! Check your email for access details.");
      setFormData({ name: "", email: "", phone: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <section ref={signupRef} className="py-24 px-6 bg-section-gradient relative overflow-hidden" data-testid="signup-section">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8B5CF6]/10 rounded-full blur-[150px]" />
      
      <div className="max-w-xl mx-auto relative">
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Get Started</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            Ready to Stream?
          </h2>
          <div className="section-divider" />
        </div>
        
        <form onSubmit={handleSubmit} className="card p-8 space-y-6 border border-white/10" data-testid="signup-form">
          <div>
            <label className="block text-white/70 text-sm mb-2">Full Name</label>
            <Input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-dark w-full"
              placeholder="John Doe"
              data-testid="signup-name"
            />
          </div>
          
          <div>
            <label className="block text-white/70 text-sm mb-2">Email Address</label>
            <Input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="input-dark w-full"
              placeholder="john@example.com"
              data-testid="signup-email"
            />
          </div>
          
          <div>
            <label className="block text-white/70 text-sm mb-2">Phone Number</label>
            <Input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="input-dark w-full"
              placeholder="+1 (555) 000-0000"
              data-testid="signup-phone"
            />
          </div>
          
          {selectedAddons.length > 0 && (
            <div className="p-4 bg-gradient-to-br from-[#1E1B4B]/30 to-transparent border border-[#D4AF37]/20">
              <p className="text-sm text-white/50 mb-2">Selected Add-ons:</p>
              <ul className="space-y-1">
                {selectedAddons.map(addon => (
                  <li key={addon} className="text-[#00D4FF] text-sm flex items-center gap-2">
                    <Check className="w-3 h-3" />
                    {addon.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Promo Code Section */}
          {promoCodesEnabled && (
            <div className="space-y-2">
              <label className="block text-white/70 text-sm">Promo Code (Optional)</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="input-dark flex-1"
                  disabled={promoApplied}
                  data-testid="promo-code-field"
                />
                <Button 
                  type="button"
                  onClick={applyPromoCode}
                  disabled={promoLoading || promoApplied}
                  className="bg-[#00D4FF]/20 border border-[#00D4FF]/30 text-[#00D4FF] hover:bg-[#00D4FF]/30"
                  data-testid="apply-promo-btn"
                >
                  {promoLoading ? "..." : promoApplied ? "Applied" : "Apply"}
                </Button>
              </div>
              
              {promoApplied && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                  <p className="font-semibold">Promo Applied: {promoApplied.code}</p>
                  <p className="flex items-center justify-between mt-1">
                    <span className="line-through text-white/40">${promoApplied.original_price}</span>
                    <span className="text-lg font-bold">${promoApplied.final_price}</span>
                  </p>
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-primary w-full"
            data-testid="signup-submit"
          >
            {loading ? "Processing..." : "Get Access"}
          </Button>
          
          <p className="text-center text-white/40 text-xs">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </form>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ faqRef }) => {
  const faqs = [
    {
      q: "What devices can I use?",
      a: "Prestigious TV works on Firestick, Android TV, Smart TVs, iOS and Android phones, tablets, and web browsers."
    },
    {
      q: "How do I get started?",
      a: "Simply sign up, complete payment, and you'll receive your login credentials instantly via email."
    },
    {
      q: "Are there any contracts?",
      a: "No contracts. No hidden fees. You can cancel anytime without penalties."
    },
    {
      q: "How many channels are included?",
      a: "Our service includes access to over 50,000 channels worldwide, including live sports, movies, TV shows, and international content."
    },
    {
      q: "What's the annual price?",
      a: "Annual access is $199/year, which gives you unlimited streaming on multiple devices."
    },
  ];
  
  return (
    <section ref={faqRef} className="py-24 px-6 bg-section-alt" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Questions?</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked
          </h2>
          <div className="section-divider" />
        </div>
        
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, idx) => (
            <AccordionItem 
              key={idx} 
              value={`faq-${idx}`}
              className="card border border-white/10 px-6 hover:border-[#00D4FF]/30 transition-colors"
              data-testid={`faq-item-${idx}`}
            >
              <AccordionTrigger className="text-left font-['Playfair_Display'] text-lg font-semibold hover:text-[#D4AF37] transition-colors py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/60 pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// FOOTER
const Footer = () => {
  return (
    <footer className="py-12 px-6 bg-[#0A0A0A] border-t border-white/10" data-testid="footer">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="font-['Playfair_Display'] text-xl font-bold text-white">
              Prestigious<span className="text-[#D4AF37]">TV</span>
            </span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-white/50">
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Terms</a>
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Contact</a>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-white/5 text-center text-white/40 text-sm">
          © 2026 Prestigious-TV | A Prestigious Companies Network | Strategic Partner of the Prestigious Travel Club Foundation
        </div>
      </div>
    </footer>
  );
};

// REVIEWS Section
const ReviewsSection = () => {
  const reviews = [
    {
      name: "Marcus Thompson",
      review: "Great service! Very good image quality where you can follow your sport events without the image jumping all the time. Many thanks for the fast delivery after purchase.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face"
    },
    {
      name: "Sarah Mitchell",
      review: "Very good service, serious, fast and efficient. I recommend without hesitation. I think this is the best streaming subscription service, thank you.",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face"
    },
    {
      name: "David Rodriguez",
      review: "Great Support! Great service! Thank you for your service. When my subscription expires, I will renew it quickly. If you are looking for someone who is reliable, Prestigious TV is it!",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face"
    }
  ];

  return (
    <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Testimonials</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            What Our Customers Say
          </h2>
          <div className="section-divider" />
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {reviews.map((review, idx) => (
            <div 
              key={idx}
              className="card p-8 border border-white/10 text-center group hover:border-[#D4AF37]/30 transition-colors"
              data-testid={`review-${idx}`}
            >
              <Quote className="w-10 h-10 text-[#D4AF37] mx-auto mb-6 opacity-50" />
              <p className="text-white/70 italic leading-relaxed mb-6">
                "{review.review}"
              </p>
              <div className="flex flex-col items-center">
                <img 
                  src={review.avatar} 
                  alt={review.name}
                  className="w-14 h-14 rounded-full border-2 border-[#D4AF37]/30 mb-3 object-cover"
                />
                <p className="font-semibold text-white uppercase tracking-wider text-sm">{review.name}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// DEVICE COMPATIBILITY Section
const DeviceCompatibilitySection = () => {
  const devices = [
    { name: "Android", desc: "Phones & Tablets", icon: "📱" },
    { name: "Apple", desc: "iPhone & iPad", icon: "🍎" },
    { name: "Xbox", desc: "Select a model", icon: "🎮" },
    { name: "Windows", desc: "Select a version", icon: "💻" },
    { name: "Amazon", desc: "Fire TV & Fire Stick", icon: "🔥" },
    { name: "LG", desc: "Smart TVs & Phones", icon: "📺" },
    { name: "Samsung", desc: "Smart TVs & Phones", icon: "📺" },
    { name: "Apple TV", desc: "Select a model", icon: "📺" },
    { name: "Chromecast", desc: "All versions", icon: "📡" },
    { name: "Android TV", desc: "All TVs", icon: "📺" },
    { name: "Nvidia Shield", desc: "Select a model", icon: "🎮" },
    { name: "MAG Boxes", desc: "Infomir devices", icon: "📦" },
  ];

  return (
    <section className="py-24 px-6 bg-section-alt" data-testid="devices-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#00D4FF] uppercase tracking-widest text-sm mb-4">Universal Access</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
            Compatible With All Devices
          </h2>
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {devices.map((device, idx) => (
            <div 
              key={idx}
              className="card p-4 border border-white/10 text-center group hover:border-[#00D4FF]/30 transition-all hover:scale-105"
              data-testid={`device-${idx}`}
            >
              <div className="text-3xl mb-2">{device.icon}</div>
              <p className="font-semibold text-white text-sm">{device.name}</p>
              <p className="text-white/50 text-xs">{device.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// MAIN LANDING PAGE
export default function LandingPage() {
  const heroRef = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);
  const signupRef = useRef(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [pricingData, setPricingData] = useState({
    annual_price: 199,
    discounts_enabled: true,
    promo_codes_enabled: true
  });

  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setPricingData(response.data);
      } catch (error) {
        console.error("Failed to fetch pricing");
      }
    };
    fetchPricing();
  }, []);
  
  const scrollTo = (section) => {
    const refs = {
      hero: heroRef,
      pricing: pricingRef,
      faq: faqRef,
      signup: signupRef
    };
    refs[section]?.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      <Navbar onScrollTo={scrollTo} />
      <HeroSection heroRef={heroRef} onScrollTo={scrollTo} />
      <CategoriesSection />
      <FeaturesSection />
      <DeviceCompatibilitySection />
      <ValueSection />
      <HowItWorksSection />
      <PricingSection pricingRef={pricingRef} onScrollTo={scrollTo} annualPrice={pricingData.annual_price} />
      <AddonsSection selectedAddons={selectedAddons} setSelectedAddons={setSelectedAddons} />
      <SignupSection 
        signupRef={signupRef} 
        selectedAddons={selectedAddons} 
        annualPrice={pricingData.annual_price}
        promoCodesEnabled={pricingData.promo_codes_enabled}
      />
      <ReviewsSection />
      <FAQSection faqRef={faqRef} />
      <Footer />
    </div>
  );
}
